"use client";

import React from "react";
import { Calendar, User, Clock, ArrowLeft, Share2, Tag, Bookmark, Heart, MessageSquare } from "lucide-react";
import { PostsProvider, usePosts, Post as PostType } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

declare const wvcClient: any;

function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, "").trim();
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return dateStr;
  }
}

function cleanContent(content?: string): string {
  if (!content) return "";
  
  let cleaned = content.replace(/\[([\w_-]+)[^\]]*\][\s\S]*?\[\/\1\]/gi, "");
  
  cleaned = cleaned.replace(/\[[\w_-]+[^\]]*\]/gi, "");
  return cleaned;
}

const samplePost: PostType = {
  id: "sample-1",
  title: "How to Turn Blog Insights into a High-Converting SEO Funnel",
  slug: "how-to-turn-blog-insights-into-high-converting-seo-funnel",
  date: "2024-10-12T10:00:00",
  excerpt: "Discover how I analyze blog performance data to construct high-converting SEO funnels that turn organic readers into qualified consultation leads.",
  content: `
    <p class="mb-6 leading-relaxed">Organic traffic is only half the equation. Generating thousands of monthly blog visitors delivers zero bottom-line value if those readers bounce without taking action. When I audit content performance for clients, my primary objective is to map blog insights directly into high-intent conversion pathways.</p>
    <div class="my-8 rounded-lg overflow-hidden border border-border shadow-md">
      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/blog_insights_high_converting_seo_funnel.png" alt="How to Turn Blog Insights into a High-Converting SEO Funnel" class="w-full h-auto object-cover" />
    </div>
    <p class="mb-6 leading-relaxed">By categorizing search queries into informational, commercial, and transactional intent stages, I design contextual bridge assets—such as targeted case study deep-dives and tailored audit offers—that guide organic visitors through a friction-free funnel.</p>
    <p class="mb-6 leading-relaxed">Continuous conversion rate optimization paired with rigorous search intent alignment ensures that every blog article works as an active client acquisition engine for your business.</p>
  `,
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/blog_insights_high_converting_seo_funnel.png",
  author: {
    id: 1,
    name: "Yassine Essaadi",
    avatar: { "96": "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_profile.png" }
  },
  categories: [{ id: 1, name: "SEO Strategy", slug: "seo-strategy", taxonomy: "category" }],
  tags: [
    { id: 10, name: "SEO Funnels", slug: "seo-funnels", taxonomy: "post_tag" },
    { id: 11, name: "Conversion Rate", slug: "conversion-rate", taxonomy: "post_tag" },
    { id: 12, name: "Analytics", slug: "analytics", taxonomy: "post_tag" }
  ]
};

function getRelatedServices(categorySlug?: string) {
  const slug = categorySlug?.toLowerCase() || "";

  if (slug === "technical-seo") {
    return [
      {
        title: "Technical SEO Services",
        description: "Hands-on technical SEO support, crawl budget optimization, and site architecture fixes.",
        linkText: "Explore technical SEO support",
        to: "/TechnicalSeoPage"
      },
      {
        title: "SEO Audits & Analysis",
        description: "In-depth technical and architectural audits to uncover hidden search roadblocks.",
        linkText: "Request a comprehensive SEO audit",
        to: "/SeoAuditsPage"
      }
    ];
  }

  if (slug === "ecommerce-seo") {
    return [
      {
        title: "Ecommerce SEO",
        description: "Category and product page search optimization built to drive sustainable revenue growth.",
        linkText: "Scale ecommerce search performance",
        to: "/EcommerceSeoPage"
      },
      {
        title: "Technical SEO",
        description: "Fix faceted navigation, canonical indexing, and structural issues across large catalogs.",
        linkText: "Strengthen technical foundations",
        to: "/TechnicalSeoPage"
      },
      {
        title: "SEO Audits",
        description: "Complete performance review identifying conversion and ranking opportunities.",
        linkText: "Get an ecommerce store audit",
        to: "/SeoAuditsPage"
      }
    ];
  }

  if (slug === "lead-generation") {
    return [
      {
        title: "B2B SEO & Lead Generation",
        description: "Target high-intent decision-makers and convert search traffic into qualified sales pipelines.",
        linkText: "Build a B2B lead generation strategy",
        to: "/B2BSeoLeadGenerationPage"
      },
      {
        title: "National SEO",
        description: "Dominate competitive high-volume queries nationwide across the UK.",
        linkText: "Expand nationwide search presence",
        to: "/NationalSeoPage"
      },
      {
        title: "International SEO",
        description: "Multi-regional and multilingual search strategies with precision hreflang architecture.",
        linkText: "Grow international organic reach",
        to: "/InternationalSeoPage"
      }
    ];
  }

  if (slug === "ai-in-seo" || slug === "ai-search") {
    return [
      {
        title: "AI Search Optimisation",
        description: "Optimize for Google AI Overviews, Perplexity, and next-generation conversational search engines.",
        linkText: "Prepare for AI-driven search",
        to: "/AISearchOptimisationPage"
      },
      {
        title: "Technical SEO",
        description: "Structured schema data, clean crawling paths, and authoritative semantic entities.",
        linkText: "Upgrade technical schema & architecture",
        to: "/TechnicalSeoPage"
      },
      {
        title: "SEO Audits",
        description: "Identify content and entity gaps that prevent AI search engines from referencing your brand.",
        linkText: "Book an AI readiness audit",
        to: "/SeoAuditsPage"
      }
    ];
  }

  if (slug === "seo-strategy" || slug === "website-optimization") {
    return [
      {
        title: "SEO Audits",
        description: "Comprehensive audits evaluating technical health, content positioning, and competitive gaps.",
        linkText: "Schedule a comprehensive SEO audit",
        to: "/SeoAuditsPage"
      },
      {
        title: "Technical SEO",
        description: "Resolve underlying platform bottlenecks, speed issues, and indexing obstacles.",
        linkText: "Implement technical SEO solutions",
        to: "/TechnicalSeoPage"
      },
      {
        title: "Freelance SEO Consultant",
        description: "Direct strategic collaboration with Yassine Essaadi on bespoke growth roadmaps.",
        linkText: "Work directly with an SEO consultant",
        to: "/FreelanceSeoConsultantPage"
      }
    ];
  }

  if (slug === "keyword-research" || slug === "on-page-seo" || slug === "onpage-seo") {
    return [
      {
        title: "SEO Audits",
        description: "Evaluate your page-level targeting, keyword intent mapping, and structural hierarchy.",
        linkText: "Get an on-page and keyword audit",
        to: "/SeoAuditsPage"
      },
      {
        title: "Freelance SEO Consultant",
        description: "Hands-on keyword positioning and content optimization tailored to your commercial goals.",
        linkText: "Partner with an SEO consultant",
        to: "/FreelanceSeoConsultantPage"
      }
    ];
  }

  if (slug === "case-studies") {
    return [
      {
        title: "Freelance SEO Consultant",
        description: "Direct strategic consultancy delivering measurable organic revenue growth.",
        linkText: "Consult with Yassine Essaadi",
        to: "/FreelanceSeoConsultantPage"
      },
      {
        title: "SEO Audits",
        description: "A data-backed baseline assessment to unlock untapped organic search opportunities.",
        linkText: "Request a detailed SEO audit",
        to: "/SeoAuditsPage"
      }
    ];
  }

  // default fallback for any other slug or missing category
  return [
    {
      title: "SEO Audits",
      description: "A deep forensic review identifying exactly why your rankings are stalling and how to fix them.",
      linkText: "Book a strategic SEO audit",
      to: "/SeoAuditsPage"
    },
    {
      title: "Freelance SEO Consultant",
      description: "Direct senior SEO consulting without agency overheads or junior account managers.",
      linkText: "Work with an independent SEO expert",
      to: "/FreelanceSeoConsultantPage"
    }
  ];
}

function RelatedServicesSection({ currentPost }: { currentPost: PostType }) {
  const firstCategorySlug = currentPost.categories?.[0]?.slug;
  const services = getRelatedServices(firstCategorySlug);

  return (
    <div className="mb-16 space-y-6 pt-2">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
        <h2 className="font-serif text-2xl font-bold text-foreground">Explore Related SEO Services</h2>
        <span className="text-xs uppercase tracking-wider font-semibold text-primary">Direct Consultation</span>
      </div>

      <div className={`grid grid-cols-1 ${services.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
        {services.map((service, i) => (
          <div
            key={service.to + i}
            data-index={i}
            className="flex flex-col justify-between p-5 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors shadow-xs"
          >
            <div className="space-y-2">
              <h3 className="font-serif text-base font-semibold text-foreground">
                {service.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {service.description}
              </p>
            </div>
            <div className="pt-4 mt-4 border-t border-border/50">
              <Button variant="link" size="sm" asChild className="p-0 h-auto text-primary font-medium">
                <Link to={service.to}>{service.linkText} →</Link>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RelatedPostsConsumer({ currentPostId }: { currentPostId?: string | number }) {
  const { posts, loading } = usePosts();

  const related = (posts || [])
    .filter((item) => String(item.id) !== String(currentPostId))
    .slice(0, 2);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[0, 1].map((n) => (
          <div key={n} data-index={n} className="h-64 rounded-xl bg-muted/40 animate-pulse border border-border" />
        ))}
      </div>
    );
  }

  if (related.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground bg-card border border-border rounded-lg">
        No other related articles available at this time.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {related.map((item, i) => (
        <Card key={item.id || i} data-index={i} className="group overflow-hidden border border-border bg-card hover:shadow-md transition-shadow">
          {item.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-muted">
              <img
                src={item.featuredImage}
                alt={item.title}
                className="size-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
          )}
          <CardHeader className="p-5">
            <CardTitle className="font-serif text-lg font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
              <Link to={item.link || `/BlogPage`}>{item.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 pt-0 space-y-4">
            {item.excerpt && (
              <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                {stripHtml(item.excerpt)}
              </p>
            )}
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
              <span>{formatDate(item.date) || "Recent"}</span>
              <Button variant="link" size="sm" asChild className="p-0 h-auto text-primary">
                <Link to={item.link || `/BlogPage`}>Read More →</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPost }: { currentPost: PostType }) {
  const categoryId = currentPost.categories && currentPost.categories.length > 0 ? currentPost.categories[0].id : undefined;

  const queryParams = categoryId
    ? { post_type: "post", posts_per_page: 4, categories: [categoryId] }
    : { post_type: "post", posts_per_page: 4 };

  const relatedWPQuery = new WP_Query(queryParams);

  return (
    <div className="space-y-6 pt-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-bold text-foreground">Related Insights</h2>
        <Button variant="ghost" size="sm" asChild className="text-primary hover:text-primary/80">
          <Link to="/BlogPage">Explore More →</Link>
        </Button>
      </div>

      <PostsProvider wp_query={relatedWPQuery}>
        <RelatedPostsConsumer currentPostId={currentPost.id} />
      </PostsProvider>
    </div>
  );
}

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8 animate-pulse">
        <div className="h-6 w-32 bg-muted rounded-md" />
        <div className="h-12 w-3/4 bg-muted rounded-md" />
        <div className="flex gap-4 items-center">
          <div className="size-10 rounded-full bg-muted" />
          <div className="space-y-2">
            <div className="h-4 w-28 bg-muted rounded" />
            <div className="h-3 w-20 bg-muted rounded" />
          </div>
        </div>
        <div className="h-96 w-full bg-muted rounded-xl" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded" />
          <div className="h-4 w-5/6 bg-muted rounded" />
          <div className="h-4 w-4/6 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto text-center py-12 px-4 bg-card rounded-xl border border-border shadow-xs">
        <p className="text-destructive font-medium mb-4">Unable to load the article details.</p>
        <Button variant="outline" onClick={() => refetch()}>
          Retry Loading
        </Button>
      </div>
    );
  }

  const currentPost = posts && posts.length > 0 ? posts[0] : samplePost;
  const sanitizedExcerpt = stripHtml(currentPost.excerpt);
  const formattedPostDate = formatDate(currentPost.date);
  const sanitizedContent = cleanContent(currentPost.content);

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between gap-4 mb-8">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-primary">
          <Link to="/BlogPage">
            <ArrowLeft className="mr-2 size-4" />
            Back to Blog
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" aria-label="Bookmark post">
            <Bookmark className="size-4 text-muted-foreground" />
          </Button>
          <Button variant="outline" size="icon" aria-label="Share post">
            <Share2 className="size-4 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {currentPost.categories && currentPost.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {currentPost.categories.map((cat, i) => (
            <span
              key={cat.id || i}
              data-index={i}
              className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-secondary text-primary"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 leading-tight">
        {currentPost.title}
      </h1>

      <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-border text-sm text-muted-foreground mb-8">
        <div className="flex items-center gap-2">
          <div className="size-9 rounded-full bg-muted flex items-center justify-center overflow-hidden border border-border">
            {currentPost.author?.avatar?.["96"] ? (
              <img
                src={currentPost.author.avatar["96"]}
                alt={currentPost.author.name || "Author"}
                className="size-full object-cover"
              />
            ) : (
              <User className="size-5 text-muted-foreground" />
            )}
          </div>
          <span className="font-medium text-foreground">{currentPost.author?.name || "Yassine Essaadi"}</span>
        </div>

        {formattedPostDate && (
          <div className="flex items-center gap-1.5">
            <Calendar className="size-4" />
            <span>{formattedPostDate}</span>
          </div>
        )}

        <div className="flex items-center gap-1.5">
          <Clock className="size-4" />
          <span>5 min read</span>
        </div>
      </div>

      {currentPost.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-lg border border-border shadow-md bg-muted">
          <img
            src={currentPost.featuredImage}
            alt={currentPost.title}
            className="w-full h-auto max-h-[500px] object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {sanitizedExcerpt && (
        <div className="text-lg sm:text-xl font-medium text-muted-foreground leading-relaxed italic mb-8 border-l-4 border-primary pl-4 py-1">
          {sanitizedExcerpt}
        </div>
      )}

      <div className="prose max-w-none text-foreground text-base sm:text-lg leading-relaxed space-y-6 mb-12">
        {sanitizedContent ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
        ) : (
          <p className="text-muted-foreground">No detailed content provided for this post.</p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 py-6 border-y border-border mb-12">
        <div className="flex flex-wrap items-center gap-2">
          <Tag className="size-4 text-muted-foreground mr-1" />
          {currentPost.tags && currentPost.tags.length > 0 ? (
            currentPost.tags.map((tag, i) => (
              <span
                key={tag.id || i}
                data-index={i}
                className="text-xs px-2.5 py-1 rounded-md bg-muted text-muted-foreground font-medium"
              >
                #{tag.name}
              </span>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">#SEO #Strategy</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" className="gap-1.5">
            <Heart className="size-4 text-primary fill-primary/20" />
            <span>Like</span>
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5">
            <MessageSquare className="size-4" />
            <span>Comment</span>
          </Button>
        </div>
      </div>

      <Card className="mb-16 bg-card border border-border shadow-sm">
        <CardContent className="p-6 flex flex-col sm:flex-row items-center sm:items-start gap-6">
          <div className="size-16 rounded-full bg-secondary flex items-center justify-center shrink-0 overflow-hidden border-2 border-primary/20">
            {currentPost.author?.avatar?.["96"] ? (
              <img
                src={currentPost.author.avatar["96"]}
                alt={currentPost.author.name || "Author"}
                className="size-full object-cover"
              />
            ) : (
              <User className="size-8 text-secondary-foreground" />
            )}
          </div>
          <div className="text-center sm:text-left space-y-2">
            <h3 className="font-serif text-lg font-semibold text-foreground">
              Written by {currentPost.author?.name || "Yassine Essaadi"}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              I am an independent SEO consultant helping businesses grow through sustainable, data-driven search strategies. You work directly with me.
            </p>
            <div className="pt-2">
              <Button variant="link" size="sm" className="p-0 h-auto text-primary">
                <Link to="/AboutMePage">Learn more about my approach →</Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <RelatedServicesSection currentPost={currentPost} />

      <RelatedPostsSection currentPost={currentPost} />
    </article>
  );
}

export default function Post() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post", posts_per_page: 3 };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      data-section-id="34820"
      id="single-post-template-section"
      data-nav="light"
      className="bg-background text-foreground py-16 sm:py-20 lg:py-24"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}